"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SkriftMark } from "@/components/skrift/skrift-logo/skrift-mark";
import { GoogleSignInButton } from "./google-sign-in-button";
import { clearOrgSelection } from "../actions";

const ERROR_MESSAGES: Record<string, string> = {
  authentication_failed: "Authentication failed. Please try again.",
  session_expired: "Your session expired. Please sign in again.",
  no_organization:
    "Your account doesn't belong to any organization. Contact your admin.",
  deactivated:
    "Your account has been deactivated. Contact your organization admin.",
};

type Organization = { organizationId: string; name: string };

function Soc2Badge() {
  return (
    <div className="flex flex-col items-center gap-1 opacity-60">
      <Image
        src="/brand/soc2-non-cpa-blk.png"
        alt="SOC 2 Type II compliance"
        width={359}
        height={357}
        className="h-12 w-auto object-contain"
      />
    </div>
  );
}

interface LoginLeftPanelProps {
  returnTo?: string;
  returnHost?: string;
  error?: string;
  organizations?: Organization[] | null;
}

export function LoginLeftPanel({
  returnTo,
  returnHost,
  error,
  organizations,
}: LoginLeftPanelProps) {
  const errorMessage = error ? ERROR_MESSAGES[error] : null;
  const showOrgSelection = organizations && organizations.length > 0;

  return (
    <div className="flex min-h-screen w-full flex-col bg-white p-8 md:min-h-0 md:w-[35%]">
      <div className="self-center">
        <SkriftMark size="md" />
      </div>

      <div className="flex flex-1 flex-col items-center justify-evenly gap-6">
        <div className="text-center space-y-4">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-shark-gray-900">
              {showOrgSelection ? "Select organization" : "Sign in to Skrift"}
            </h1>
            <p className="text-sm text-shark-gray-500 max-w-xs">
              {showOrgSelection
                ? "Your account belongs to multiple organizations. Choose one to continue."
                : "AI-powered customer intelligence for your post-sales team."}
            </p>
          </div>

          {errorMessage && (
            <p className="text-sm text-carnation-800 max-w-xs">
              {errorMessage}
            </p>
          )}

          {showOrgSelection ? (
            <div className="flex flex-col gap-3 w-full max-w-xs">
              <div className="overflow-hidden rounded-xl border border-shark-gray-200 bg-shark-gray-50">
                {organizations.map((org, index) => (
                  <button
                    key={org.organizationId}
                    className={`group flex w-full items-center justify-between px-4 py-3.5 text-left transition-colors hover:login-org-hover ${
                      index < organizations.length - 1
                        ? "border-b border-shark-gray-200"
                        : ""
                    }`}
                    onClick={() => {
                      window.location.href = `/api/auth/organization-selection?organization_id=${org.organizationId}`;
                    }}
                  >
                    <span className="text-sm font-medium text-shark-gray-900 group-hover:login-text-gradient">
                      {org.name}
                    </span>
                    <ChevronRight className="h-4 w-4 text-shark-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-pink-500" />
                  </button>
                ))}
              </div>
              <form action={clearOrgSelection}>
                <Button
                  type="submit"
                  variant="ghost"
                  size="sm"
                  className="text-shark-gray-400"
                >
                  ← Back to sign in
                </Button>
              </form>
            </div>
          ) : (
            <GoogleSignInButton returnTo={returnTo} returnHost={returnHost} />
          )}
        </div>

        <Soc2Badge />
      </div>

      <div className="flex flex-col items-center gap-6">
        <div className="flex w-full min-w-0 flex-col items-center gap-3">
          <nav className="flex w-full min-w-0 flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs">
            <Link
              href="https://skrift.ai/legal/terms-of-service/"
              className="shrink-0 whitespace-nowrap text-shark-gray-400 transition-colors hover:text-shark-gray-600"
            >
              Terms of Service
            </Link>
            <Link
              href="https://skrift.ai/legal/privacy-policy/"
              className="shrink-0 whitespace-nowrap text-shark-gray-400 transition-colors hover:text-shark-gray-600"
            >
              Privacy Policy
            </Link>
            <Link
              href="https://skrift.ai/legal/cookie-policy/"
              className="shrink-0 whitespace-nowrap text-shark-gray-400 transition-colors hover:text-shark-gray-600"
            >
              Cookie Policy
            </Link>
            {/* <Link
              href=""
              className="shrink-0 whitespace-nowrap text-shark-gray-400 transition-colors hover:text-shark-gray-600"
            >
              Help Center
            </Link> */}
          </nav>
          <p className="text-xs text-shark-gray-400">
            &copy; {new Date().getFullYear()} Skrift. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
